import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../store/authStore';
import Button from './Button';
import { X, Upload, Image, File, FileText, Paperclip } from 'lucide-react';
import supabase from "../lib/supabase";
import { StorageService, useFileUpload, type StorageFile } from '../lib/storage';

const schema = z.object({
  title: z.string().optional(),
  content: z.string().min(10, 'Content must be at least 10 characters'),
  category_id: z.string().min(1, 'Please select a category'),
});

type FormData = z.infer<typeof schema>;

interface CreatePostModalProps {
  communityId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreatePostModal({ communityId, onClose, onSuccess }: CreatePostModalProps) {
  const { user } = useAuthStore();
  const { uploadFiles, isUploading, uploadError, clearError } = useFileUpload();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [attachments, setAttachments] = useState<StorageFile[]>([]);
  
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      category_id: '',
    }
  });

  // Fetch categories on mount
  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        setError(null);

        // First ensure categories exist by checking count
        const { count, error: countError } = await supabase
          .from('community_categories')
          .select('*', { count: 'exact', head: true });

        if (countError) throw countError;

        // If no categories exist, insert default ones
        if (count === 0) {
          const { error: insertError } = await supabase
            .from('community_categories')
            .insert([
              { name: 'General', description: 'General discussion topics' },
              { name: 'Announcements', description: 'Important community announcements' },
              { name: 'Events', description: 'Event-related discussions' },
              { name: 'Collaborations', description: 'Collaboration opportunities' },
              { name: 'Resources', description: 'Shared resources and tools' },
              { name: 'Other', description: 'Miscellaneous topics' }
            ]);

          if (insertError) throw insertError;
        }

        // Now fetch all categories
        const { data, error } = await supabase
          .from('community_categories')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length || !user) return;

    try {
      clearError();
      const fileArray = Array.from(files);
      
      const uploadedFiles = await uploadFiles(fileArray, {
        userId: user.id,
        contextId: communityId,
        contextType: 'community'
      });

      // Generate signed URLs for preview
      const filesWithSignedUrls = await Promise.all(
        uploadedFiles.map(async (file) => {
          try {
            if (file.type.startsWith('image/')) {
              const signedUrl = await StorageService.getSignedUrl('community-attachments', file.path);
              return { ...file, signedUrl };
            }
            return file;
          } catch (error) {
            console.error('Error generating signed URL for preview:', error);
            return file;
          }
        })
      );

      setAttachments(prev => [...prev, ...filesWithSignedUrls]);
    } catch (error: any) {
      console.error('Upload error:', error);
      // Error is handled by the useFileUpload hook
    } finally {
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = async (index: number) => {
    try {
      const attachment = attachments[index];
      
      // Remove from storage
      await StorageService.removeFile('community-attachments', attachment.path);

      // Remove from local state
      setAttachments(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error removing attachment:', error);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const { data: newPost, error: insertError } = await supabase
        .from('community_posts')
        .insert({
          community_id: communityId,
          author_id: user.id,
          title: data.title || null,
          content: data.content,
          category_id: data.category_id,
          attachments: attachments.length > 0 ? attachments : null
        })
        .select(`
          *,
          author:author_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          ),
          category:category_id(
            id,
            name
          )
        `)
        .single();

      if (insertError) throw insertError;

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to create post:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAttachmentPreview = (attachment: StorageFile, index: number) => {
    const isImage = attachment.type.startsWith('image/');
    const isPdf = attachment.type === 'application/pdf';
    const isText = attachment.type.includes('text') || attachment.type.includes('document');
    
    return (
      <div key={index} className="relative group">
        <div className="flex items-center p-2 bg-surface-light rounded-lg">
          {isImage ? (
            <div className="w-12 h-12 mr-2 relative">
              {attachment.signedUrl ? (
                <img 
                  src={attachment.signedUrl}
                  alt={attachment.name} 
                  className="w-full h-full object-cover rounded-md"
                  onError={(e) => {
                    console.error('Image failed to load:', attachment.signedUrl);
                    // Hide the image if it fails to load
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-surface rounded-md flex items-center justify-center">
                  <Image className="w-6 h-6 text-neutral" />
                </div>
              )}
            </div>
          ) : (
            <div className="w-10 h-10 flex items-center justify-center mr-2 bg-surface rounded-md">
              {isPdf ? (
                <FileText className="w-5 h-5 text-secondary-dark" />
              ) : isText ? (
                <File className="w-5 h-5 text-accent" />
              ) : (
                <Paperclip className="w-5 h-5 text-neutral" />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-primary truncate">{attachment.name}</div>
            <div className="text-xs text-neutral">
              {(attachment.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <button
            type="button"
            onClick={() => removeAttachment(index)}
            className="p-1 text-neutral hover:text-secondary-dark rounded-full"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-10">
      <div className="bg-surface-light rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-secondary border-b border-secondary p-4 flex items-center justify-between shadow-md z-10 mb-6">
          <h2 className="text-3xl font-bold font-dillan text-neutral">Create New Post</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          {(error || uploadError) && (
            <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
              {error || uploadError}
            </div>
          )}

          <div>
            <input
              type="text"
              {...register('title')}
              className="w-full px-3 py-2 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillan"
              placeholder="Enter post title"
            />
          </div>

          <div>
            <textarea
              {...register('content')}
              className="w-full px-3 py-2 pl-4 pt-3 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillan"
              placeholder="What's on your mind?"
            />
            {errors.content && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.content.message}</p>
            )}
          </div>

          <div>
            <select
              {...register('category_id')}
              className="w-full px-3 py-2 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillan"
              disabled={isLoadingCategories}
            >
              <option value="" disabled>
                {isLoadingCategories ? 'Loading categories...' : 'Select a category'}
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {errors.category_id && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.category_id.message}</p>
            )}
          </div>

          {/* File Attachments */}
          <div>
            <div className="space-y-2">
              {attachments.map((attachment, index) => renderAttachmentPreview(attachment, index))}
              <button
                type="button"
                onClick={handleFileClick}
                disabled={isUploading}
                className="w-full h-12 px-4 rounded-full border border-neutral border-dashed bg-surface-light text-neutral hover:text-primary hover:border-accent transition-colors flex items-center justify-center"
              >
                <Upload className="w-5 h-5 mr-2" />
                {isUploading ? 'Uploading...' : 'Add Attachments'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                disabled={isUploading}
                multiple
                accept="image/*,application/pdf,.doc,.docx,.txt"
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 bg-secondary text-neutral hover:bg-primary-light hover:text-primary border border-secondary hover:border-primary-light"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1 bg-secondary text-neutral hover:bg-primary-light hover:text-primary border border-secondary hover:border-primary-light"
              disabled={isSubmitting || isLoadingCategories || isUploading}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}